import type { ILocation } from '@/Interfaces/Forecaster';
import type { IFencingLocation } from '@/Interfaces/Location.interface';
import GeoFencing from '@/components/Geography/GeoFencing';
import useGeoFencing from '@/components/Geography/GeoFencing/useGeoFencing';
import LocationChips from '@/components/Geography/LocationChips';
import GeoMap from '@/components/Map/GeoMap';
import Switch from '@/components/Switch/index';
import { LINE_ITEM_GEO_KEYS } from '@/constants/lineItems/lineItemGeoKeys';
import { getTypeFromRadius } from '@/helpers/forecaster/geography';
import { fetchTargetingLocations, fetchTargetingLocationsLookup } from '@/store/api/geo.api';
import { ArrowDropDown } from '@mui/icons-material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Button, Chip, Collapse, FormLabel } from '@mui/material';
import debounce from 'debounce';
import { useCallback, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import SearchLocations from './SearchLocations';

interface IProps {
  index: number;
}

const GeoSection = ({ index }: IProps) => {
  const { setValue, getValues } = useFormContext();

  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ILocation[]>([]);
  const [notFoundLocations, setNotFoundLocations] = useState<ILocation[]>([]);
  const [missingBoundaryIds, setMissingBoundaryIds] = useState<Set<ILocation['id']>>(new Set());
  const [isGeoExclusionOpen, setIsGeoExclusionOpen] = useState(false);

  const [includedLocations, excludedLocations, geoFence] = useWatch({
    name: [LINE_ITEM_GEO_KEYS.GEO_INCLUDED, LINE_ITEM_GEO_KEYS.GEO_EXCLUDED, LINE_ITEM_GEO_KEYS.GEO_FENCE],
  }) as [ILocation[], ILocation[], IFencingLocation | null];

  const [isRadiusMode, setIsRadiusMode] = useState(!!geoFence);
  const isInitialMount = useRef(true);

  const handleGeoFenceChange = useCallback(
    (newGeoFence: IFencingLocation | null) => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      setValue(LINE_ITEM_GEO_KEYS.GEO_FENCE, newGeoFence, { shouldDirty: true });
    },
    [setValue],
  );

  const geoFencingState = useGeoFencing({
    initialGeoFence: geoFence,
    onGeoFenceChange: handleGeoFenceChange,
  });

  const clearAndToggle = useCallback(() => {
    setIsRadiusMode(prev => {
      const newMode = !prev;
      setValue(LINE_ITEM_GEO_KEYS.GEO_INCLUDED, [], { shouldDirty: true });
      setValue(LINE_ITEM_GEO_KEYS.GEO_EXCLUDED, [], { shouldDirty: true });
      setValue(LINE_ITEM_GEO_KEYS.GEO_FENCE, null, { shouldDirty: true });
      geoFencingState.handleClear();
      return newMode;
    });
  }, [setValue, geoFencingState]);

  const handleModeToggle = useCallback(() => {
    const hasData = isRadiusMode
      ? !!geoFence
      : (includedLocations ?? []).length > 0 || (excludedLocations ?? []).length > 0;

    if (hasData) {
      const confirmed = window.confirm('Switching modes will clear all current geo selections. Continue?');
      if (!confirmed) return;
    }
    clearAndToggle();
  }, [isRadiusMode, geoFence, includedLocations, excludedLocations, clearAndToggle]);

  const handleRadiusClear = useCallback(() => {
    geoFencingState.handleClear();
    setValue(LINE_ITEM_GEO_KEYS.GEO_FENCE, null, { shouldDirty: true });
  }, [geoFencingState, setValue]);

  const fetchSuggestions = useCallback(
    async (search: string, isGeoExclusion = false) => {
      setLoading(true);
      const otherLocations: ILocation[] = isGeoExclusion ? includedLocations : excludedLocations;
      const excludeTypes = [...new Set((otherLocations ?? []).map(({ type }) => type))];
      const searchSuggestions = await fetchTargetingLocations({ search, excludeTypes });
      const alreadySelectedIds = new Set(
        [...(includedLocations ?? []), ...(excludedLocations ?? [])].map((location: ILocation) => location.id),
      );
      setSuggestions(searchSuggestions.filter(location => !alreadySelectedIds.has(location.id)));
      setLoading(false);
    },
    [includedLocations, excludedLocations],
  );

  const getDebouncedHandleSearch = useCallback(
    (isGeoExclusion = false) =>
      debounce(search => {
        fetchSuggestions(search, isGeoExclusion);
      }, 350),
    [fetchSuggestions],
  );

  const handleSelectionChange = useCallback(
    (key: string) => (locations: ILocation[]) => {
      if (!locations.length) return;

      const otherLocations = key === LINE_ITEM_GEO_KEYS.GEO_INCLUDED ? excludedLocations : includedLocations;
      const otherLocationsIds = new Set(((otherLocations ?? []) as ILocation[]).map(({ id }) => id));

      setValue(
        key,
        locations.filter(({ id }) => !otherLocationsIds.has(id)),
        { shouldDirty: true },
      );
    },
    [setValue, includedLocations, excludedLocations],
  );

  const handleLocationRemove = useCallback(
    (key: string) => (id: ILocation['id']) => () => {
      const locations = getValues(key);
      const newLocations = locations.filter((location: ILocation) => location.id !== id);
      setValue(key, newLocations, { shouldDirty: true });
    },
    [setValue, getValues],
  );

  const handleClearSelection = useCallback(
    (key: string) => () => {
      setValue(key, [], { shouldDirty: true });
    },
    [setValue],
  );

  const handleCSVUpload = useCallback(
    async (zipCodes: string[]) => {
      if (!zipCodes?.length) {
        return;
      }

      const presentZips = [...(includedLocations ?? []), ...(notFoundLocations ?? [])].map(({ apiName }) => apiName);
      const { found, notFound } = await fetchTargetingLocationsLookup({
        zips: zipCodes.filter(zip => !presentZips.includes(zip)),
      });
      setValue(LINE_ITEM_GEO_KEYS.GEO_INCLUDED, [...(includedLocations ?? []), ...found], {
        shouldDirty: true,
      });
      setNotFoundLocations([...notFoundLocations, ...notFound]);
    },
    [setValue, includedLocations, notFoundLocations],
  );

  const handleGeoExclusionToggle = useCallback(() => {
    setIsGeoExclusionOpen(!isGeoExclusionOpen);
  }, [isGeoExclusionOpen]);

  const handleMissingBoundaries = useCallback((missingIds: string[]) => {
    setMissingBoundaryIds(prev => {
      const newIds = missingIds.filter(id => !prev.has(id));
      return newIds.length ? new Set([...prev, ...newIds]) : prev;
    });
  }, []);

  const missingBoundaryLocations = (includedLocations ?? []).filter(({ id }: ILocation) => missingBoundaryIds.has(id));

  const onClearNotFound = useCallback(() => {
    setNotFoundLocations([]);
  }, [setNotFoundLocations]);

  const handleNotFoundLocationRemove = useCallback(
    (id: ILocation['id']) => () => {
      setNotFoundLocations(prev => prev.filter(loc => loc.id !== id));
    },
    [],
  );

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex gap-2 items-center'>
        <FormLabel htmlFor='isRadiusMode' sx={{ margin: 0, fontWeight: 600 }}>
          Radius Targeting
        </FormLabel>
        <Switch id='isRadiusMode' checked={isRadiusMode} onChange={handleModeToggle} />
      </div>

      {isRadiusMode ? (
        <>
          <GeoFencing
            address={geoFencingState.address}
            setAddress={geoFencingState.setAddress}
            radius={geoFencingState.radius}
            suggestions={geoFencingState.suggestions}
            handleAddressInput={geoFencingState.handleAddressInput}
            handleAddressSelect={geoFencingState.handleAddressSelect}
            handleRadiusInput={geoFencingState.handleRadiusInput}
          />
          <LocationChips
            locations={[]}
            variant='success'
            geoFenceLocation={geoFence?.address}
            radiusChip={getTypeFromRadius(geoFence?.radius)}
            onGeoFenceLocationRemove={handleRadiusClear}
          />
        </>
      ) : (
        <>
          <SearchLocations
            id={`${LINE_ITEM_GEO_KEYS.GEO_INCLUDED}-${index}`}
            selectedLocations={includedLocations}
            loading={loading}
            suggestions={suggestions}
            handleSearch={getDebouncedHandleSearch()}
            handleSelectionChange={handleSelectionChange(LINE_ITEM_GEO_KEYS.GEO_INCLUDED)}
            handleClearSelection={handleClearSelection(LINE_ITEM_GEO_KEYS.GEO_INCLUDED)}
            handleLocationRemove={handleLocationRemove(LINE_ITEM_GEO_KEYS.GEO_INCLUDED)}
            handleCSVUpload={handleCSVUpload}
            handleNotFoundLocations={setNotFoundLocations}
          />

          <div className='border border-lightgray rounded-lg flex flex-col'>
            <Button
              onClick={handleGeoExclusionToggle}
              endIcon={
                <ArrowDropDown
                  sx={[
                    {
                      transition: '0.3s',
                    },
                    isGeoExclusionOpen
                      ? {
                          transform: 'rotate(180deg)',
                        }
                      : {
                          transform: 'rotate(0)',
                        },
                  ]}
                />
              }
              fullWidth
              sx={{
                justifyContent: 'space-between',
                backgroundColor: 'grey.100',
              }}
            >
              <div className='flex items-center gap-2'>
                <RemoveCircleOutlineIcon />
                Geo Exclusion
              </div>
            </Button>
            <Collapse in={isGeoExclusionOpen}>
              <SearchLocations
                className='p-2'
                id={`${LINE_ITEM_GEO_KEYS.GEO_EXCLUDED}-${index}`}
                selectedLocations={excludedLocations}
                loading={loading}
                suggestions={suggestions}
                handleSearch={getDebouncedHandleSearch(true)}
                handleSelectionChange={handleSelectionChange(LINE_ITEM_GEO_KEYS.GEO_EXCLUDED)}
                handleClearSelection={handleClearSelection(LINE_ITEM_GEO_KEYS.GEO_EXCLUDED)}
                handleLocationRemove={handleLocationRemove(LINE_ITEM_GEO_KEYS.GEO_EXCLUDED)}
                handleNotFoundLocations={setNotFoundLocations}
              />
            </Collapse>
          </div>
        </>
      )}

      {!!notFoundLocations.length && (
        <div className='flex gap-2 items-center print:hidden'>
          <p className='text-grey text-nowrap'>Not found:</p>
          <LocationChips locations={notFoundLocations} variant='error' onRemove={handleNotFoundLocationRemove} />
          <Chip label='Clear not found' onClick={onClearNotFound} variant='outlined' />
        </div>
      )}

      {!!missingBoundaryLocations.length && (
        <p className='text-grey print:hidden'>
          Map preview unavailable for {missingBoundaryLocations.map(({ displayName }) => displayName).join(', ')} -
          still included in targeting.
        </p>
      )}

      <div className='w-full h-[450px] geo-map'>
        <GeoMap
          // @ts-expect-error js component props
          locationsToShow={includedLocations}
          excludedLocations={excludedLocations}
          reportNotFoundLocations={handleMissingBoundaries}
          geoFence={geoFence}
          disableHover={true}
        />
      </div>
    </div>
  );
};

export default GeoSection;
